import type { Currency } from "./types";

export const CURRENCY_LABEL: Record<Currency, string> = {
  DOP: "RD$",
  USD: "US$",
};

function groupNumber(value: number, decimals: number): string {
  return new Intl.NumberFormat("es-DO", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/** Versión compacta: hasta 2 decimales, sin ceros sobrantes. */
function abbrevNumber(value: number): string {
  return new Intl.NumberFormat("es-DO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Formatea un monto en su moneda. Los montos grandes se abrevian
 * automáticamente (K / M / B) para que nunca se desborden de las tarjetas.
 */
export function money(amount: number, currency: Currency = "DOP"): string {
  const value = Number.isFinite(amount) ? amount : 0;
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  const label = CURRENCY_LABEL[currency];
  if (abs >= 1_000_000_000) return `${sign}${label}${abbrevNumber(abs / 1_000_000_000)}B`;
  if (abs >= 1_000_000) return `${sign}${label}${abbrevNumber(abs / 1_000_000)}M`;
  if (abs >= 100_000) return `${sign}${label}${abbrevNumber(abs / 1_000)}K`;
  return `${sign}${label}${groupNumber(abs, 2)}`;
}

export function compactMoney(amount: number, currency: Currency = "DOP"): string {
  const value = Number.isFinite(amount) ? amount : 0;
  const formatted = new Intl.NumberFormat("es-DO", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(value));
  const sign = value < 0 ? "-" : "";
  return `${sign}${CURRENCY_LABEL[currency]}${formatted}`;
}

export function number(value: number, decimals = 2): string {
  const v = Number.isFinite(value) ? value : 0;
  return new Intl.NumberFormat("es-DO", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(v);
}

export function formatDate(iso: string, withTime = false): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const base = d.toLocaleDateString("es-DO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  if (!withTime) return base;
  const time = d.toLocaleTimeString("es-DO", {
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${base} · ${time}`;
}

export function todayISO(): string {
  const d = new Date();
  const off = d.getTimezoneOffset();
  const local = new Date(d.getTime() - off * 60000);
  return local.toISOString().slice(0, 10);
}

export function daysUntil(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  if (Number.isNaN(target.getTime())) return null;
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - now.getTime()) / 86400000);
}

export function uid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Convierte un importe de su moneda original a la moneda de presentación. */
export function convert(
  amount: number,
  from: Currency,
  to: Currency,
  rate: number,
  storedRate?: number | null,
): number {
  if (from === to) return amount;
  const r = storedRate && storedRate > 0 ? storedRate : rate;
  if (!r || r <= 0) return amount;
  if (from === "USD" && to === "DOP") return amount * r;
  if (from === "DOP" && to === "USD") return amount / r;
  return amount;
}